<template>
  <RevButton
    class="min-w-[58px]"
    :class="{ voted: voteStatus === 'up' }"
    data-test="review-vote-button"
    :disabled="voteStatus === 'up'"
    size="small"
    variant="secondary"
    @click="vote"
  >
    <div class="flex items-center gap-2">
      <!-- since we might increment the count if the user had already voted, we ignore hydration mismatch errors-->
      <span
        :class="{
          textAnimation: voteStatus === 'up',
        }"
        data-allow-mismatch="text"
        >{{ displayedVotesCount }}</span
      >
      <IconThumbsUpOutlined
        :class="{
          iconAnimation: voteStatus === 'up',
        }"
        size="16"
      />
    </div>
  </RevButton>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { IconThumbsUpOutlined } from '@ds/icons/IconThumbsUpOutlined'
import { type StorageLike, useStorage } from '@vueuse/core'

import { VOTED_REVIEWS_STORAGE_INJECTION_KEY } from './voted-reviews-storage'

const voteStatus = ref<'up' | null>(null)

const { reviewId, votesCount = 0 } = defineProps<{
  reviewId: string
  votesCount: number
}>()

const $httpFetch = useHttpFetch()

let hadPreviouslyVotedForReview = false

const displayedVotesCount = computed(() => {
  // If the user already upvoted for the review, we display the count+1
  // We do this because the /reviews endpoint has a long cache TTL
  // We make sure that whenever the user comes back on the page, the count is incremented to avoid confusion
  if (hadPreviouslyVotedForReview) {
    return votesCount + 1
  }

  return voteStatus.value === 'up' ? votesCount + 1 : votesCount
})

const { openErrorToast } = useTheToast()

/**
 * The inject is only used in the test ReviewDisplay.nuxt.spec.ts
 * In the application, no storage is provided. The storage will be nothing during SSR and localStorage on the browser.
 * This is handled directly by vueuse/useStorage. See https://github.com/vueuse/vueuse/blob/main/packages/core/useStorage/index.ts
 */
const storage = inject<StorageLike>(VOTED_REVIEWS_STORAGE_INJECTION_KEY)
const reviewsVoted = useStorage<string[]>('review-votes', [], storage)

/**
 * We perform optimistic update.
 * We immediately modify the UI
 * We store the previous values and reset them if ever the API call fails
 */
async function vote() {
  // Previous state
  const previousVoteStatus = voteStatus.value
  const previousReviewsVoted = [...reviewsVoted.value]

  // Optimistically update state
  voteStatus.value = 'up'
  reviewsVoted.value.push(reviewId)

  try {
    await $httpFetch('/reviews/v1/products/reviews/:uuid/vote', {
      pathParams: { uuid: reviewId },
      method: 'POST',
    })
  } catch {
    // Reset state when the API call fails
    voteStatus.value = previousVoteStatus
    reviewsVoted.value = previousReviewsVoted
    openErrorToast()
  }
}

onMounted(() => {
  if (reviewsVoted.value.includes(reviewId)) {
    hadPreviouslyVotedForReview = true
    voteStatus.value = 'up'
  }
})
</script>

<style scoped>
@keyframes upAndDown {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes fontSizeChange {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.iconAnimation {
  animation: upAndDown 0.5s;
}

.voted {
  @apply bg-static-success-low  border-static-success-mid text-static-success-hi;
  transition: background-color 1000ms linear;
  transition: text-color 1000ms linear;
}

.textAnimation {
  animation: fontSizeChange 0.5s;
}
</style>
